<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-venus-mars'"
      :section-title="$t('configuration.sections.gender')"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <template slot="shortcut-no-selected">
        <div class="d-flex mb-3">
          <div class="mr-2 px-1">
            <span class="shortcut px-2 py-1">Alt</span>+<span class="shortcut px-2 py-1">F</span>
          </div>
          <div>{{ $t('configuration.actions.codificationSearcher') }}</div>
        </div>
      </template>
      <template slot="voice-no-selected">
        <div class="d-flex mb-3 px-2">
          <div class="mr-2 px-1">
            <i
              class="alt-primary far fa-comment-dots"
              aria-hidden="true"
            />
          </div>
          <div>{{ $t('configuration.actions.codificationSearcher') }}</div>
        </div>
      </template>
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteGenders(selectedRows)"
        @shortkey="deleteGenders(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showGenderModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showGenderModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showGenderModal($t('configuration.actions.add'))"
        @shortkey="showGenderModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        pagination
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="genderModalWrapper">
      <gender-modal
        :action-title="actionTitle"
        @update="patchGenders($event)"
        @create="createGender($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsGenderGrid from '@/modules/configurationModule/detail/demography/gender/columnsGenderGrid'
import GenderModal from '@/modules/configurationModule/detail/demography/gender/GenderModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import GenderConfigurationServices from '@/api/resources/configuration/demography/gender/GenderConfigurationServices'

export default {
    name: 'DemographyGenderGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        GenderModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.demography.gender',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsGenderGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveGender() {
            return this.selectedRows.find(gender => gender.active)
        },
        hasDeactiveGender() {
            return this.selectedRows.find(gender => !gender.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(gender => gender.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(gender => !gender.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveGender && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveGender && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const gendersToPatch = selectionBatch.filter(gender => gender.active !== option).map(gender => {
                return {
                    active: option,
                    id: gender.id
                }
            })
            if (gendersToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(GenderConfigurationServices.patchGendersBatch(gendersToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (gendersToPatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: gendersToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchGenders(...genders) {
            const gendersPatchInBatchRequests = genders.map(gender => {
                return {
                    active: gender.active,
                    code: gender.code,
                    id: gender.id,
                    nameTranslations: gender.nameTranslations.map(nameTranslation => {
                        return {
                            languageId: nameTranslation.language.id,
                            notes: nameTranslation.notes,
                            value: nameTranslation.value
                        }
                    })
                }
            })
            const response = await this.$_toCallUnique(GenderConfigurationServices.patchGendersBatch(gendersPatchInBatchRequests))
            if (response) {
                this.$refs.genderModalWrapper.hide()
                this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated'))
                this.reloadData()
            }
        },
        async createGender(gender) {
            const genderCreateRequest = {
                active: gender.active,
                code: gender.code,
                nameTranslations: gender.nameTranslations.map(nameTranslation => {
                    return {
                        languageId: nameTranslation.language.id,
                        notes: nameTranslation.notes,
                        value: nameTranslation.value
                    }
                })
            }
            const response = await this.$_toCallUnique(GenderConfigurationServices.createGender(genderCreateRequest))
            if (response) {
                this.$refs.genderModalWrapper.hide()
                this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteGendersBatch(genders) {
            const gendersToDelete = genders.map(gender => gender.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(GenderConfigurationServices.deleteGendersBatch(gendersToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (gendersToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: gendersToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(GenderConfigurationServices))
        },
        getContextMenuItems(params) {
            return [
                {
                    name: this.$t('configuration.actions.update'),
                    cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                    action: () => this.showGenderModal(this.$t('configuration.actions.update'), params?.node.data)
                },
                {
                    name: this.$t('configuration.actions.delete'),
                    cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                    action: () => this.deleteGenders(this.selectedRows)
                },
                {
                    name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                    cssClasses: this.selectedRows.length ? ['not-show'] : '',
                    action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                },
                {
                    name: this.$t('configuration.actions.activate'),
                    cssClasses: this.canActivateBatch ? '' : ['not-show'],
                    action: () => this.activateDeactivateSelection(true, this.selectedRows)
                },
                {
                    name: this.$t('configuration.actions.deactivate'),
                    cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                    action: () => this.activateDeactivateSelection(false, this.selectedRows)
                }
            ]
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(GenderConfigurationServices))
        },
        showGenderModal(title, selectedGender) {
            this.actionTitle = title
            this.$refs.genderModalWrapper.show([selectedGender])
        },
        async deleteGenders(genders) {
            if (genders.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteGendersBatch(genders)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
